<template>
  <div class="composer">
    <div class="chat-input-wrapper">
      <textarea
        ref="ta"
        class="chat-input"
        :value="modelValue"
        :placeholder="placeholder"
        rows="1"
        :aria-label="placeholder"
        @input="onInput"
        @keydown.enter.exact.prevent="onEnter"
      ></textarea>
      <div class="right-icons">
        <button
          v-if="!loading"
          class="send-btn"
          @click.prevent="onSend"
          :disabled="!modelValue.trim()"
          aria-label="Gönder"
          title="Gönder"
        >↑</button>
        <button
          v-else
          class="stop-btn"
          @click.prevent="$emit('stop')"
          aria-label="Durdur"
          title="Durdur (ESC)"
        >
          <span class="material-symbols-outlined">stop_circle</span>
        </button>
      </div>
    </div>

    <div
      v-if="modelValue.length > warnAt"
      class="char-counter"
      :class="{ 'over-limit': modelValue.length > maxChars }"
    >
      {{ modelValue.length.toLocaleString('tr-TR') }} / {{ maxChars.toLocaleString('tr-TR') }}
    </div>

    <div class="model-selector-row">
      <span class="model-label">Model:</span>
      <div class="model-toggle" role="group" aria-label="Model seçimi">
        <button
          v-for="opt in modelOptions"
          :key="opt.value"
          class="model-btn"
          :class="{ active: selectedModel === opt.value, disabled: opt.disabled }"
          :aria-pressed="selectedModel === opt.value"
          :disabled="opt.disabled"
          @click="!opt.disabled && $emit('select-model', opt.value)"
          :title="opt.disabled ? opt.label + ' (yakında)' : opt.label"
        >{{ opt.label }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { MAX_QUESTION_CHARS, QUESTION_CHARS_WARN_AT } from "@/utils/chatLimits";

export default {
  name: "ChatComposer",
  data() {
    return {
      maxChars: MAX_QUESTION_CHARS,
      warnAt: QUESTION_CHARS_WARN_AT,
    };
  },
  props: {
    modelValue: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    selectedModel: { type: String, default: "" },
    modelOptions: { type: Array, default: () => [] },
    placeholder: {
      type: String,
      default:
        "Yasa,Tüzük ve Yüksek Mahkeme kararları ile ilgili sorularınızı sorabilirsiniz",
    },
  },
  emits: ["update:modelValue", "send", "stop", "select-model"],
  watch: {
    // Parent metni sıfırlayınca (gönderim sonrası) textarea tekrar küçülsün.
    modelValue() {
      this.$nextTick(this.autoGrow);
    },
  },
  mounted() {
    this.autoGrow();
  },
  methods: {
    onInput(e) {
      this.$emit("update:modelValue", e.target.value);
      this.autoGrow();
    },
    onEnter() {
      // Enter = gönder (Shift+Enter @keydown.exact sayesinde buraya düşmez = yeni satır).
      if (this.loading || !this.modelValue.trim()) return;
      this.$emit("send");
    },
    onSend() {
      if (!this.modelValue.trim()) return;
      this.$emit("send");
    },
    autoGrow() {
      const ta = this.$refs.ta;
      if (!ta) return;
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
    },
  },
};
</script>

<style scoped>
/* CSS değişkenleri (--sage-*) DOM üzerinden .chat-page'den miras alınır. */
.composer {
  width: 100%;
}

.chat-input-wrapper {
  width: 100%;
  display: flex;
  align-items: flex-end;
  background: var(--sage-bg-main);
  border-radius: 8px;
  padding: 6px 8px 6px 4px;
  border: 2px solid var(--sage-border);
  box-shadow: 0 4px 12px rgba(90, 98, 112, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.chat-input-wrapper:focus-within {
  border-color: var(--sage-primary);
  box-shadow: 0 4px 20px rgba(90, 98, 112, 0.12);
}

.chat-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  font-family: inherit;
  line-height: 1.5;
  padding: 11px 14px;
  color: var(--sage-text-primary);
  background: transparent;
  resize: none;
  max-height: 160px;
  overflow-y: auto;
}
.chat-input::placeholder { color: var(--sage-text-muted); }

.right-icons { display: flex; align-items: center; gap: 6px; }

.send-btn {
  width: 44px;
  height: 44px;
  border-radius: 22px;
  border: 2px solid var(--gold);
  background: var(--sage-primary);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.2s ease, box-shadow 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.send-btn:hover:not(:disabled) {
  background: var(--sage-primary-dark);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(90, 98, 112, 0.35);
}
.send-btn:active { transform: scale(0.97); }
.send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.stop-btn {
  width: 44px;
  height: 44px;
  border-radius: 22px;
  border: 2px solid #dc2626;
  background: #dc2626;
  color: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.2s ease, box-shadow 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: pulse-stop 1.5s ease-in-out infinite;
}
.stop-btn .material-symbols-outlined { font-size: 22px; }
.stop-btn:hover {
  background: #b91c1c;
  border-color: #b91c1c;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.35);
}
.stop-btn:active { transform: scale(0.97); }

@keyframes pulse-stop {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
}

.char-counter {
  font-size: 11px;
  color: #999;
  text-align: right;
  padding: 4px 4px 0;
}
.char-counter.over-limit {
  color: #e53935;
  font-weight: 600;
}

.model-selector-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  justify-content: center;
}
.model-label {
  font-size: 12px;
  color: var(--sage-text-muted);
  user-select: none;
}
.model-toggle { display: flex; gap: 4px; }
.model-btn {
  padding: 6px 14px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  background: transparent;
  color: var(--sage-text-muted);
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1.4;
}
.model-btn:hover {
  border-color: var(--sage-green);
  color: var(--sage-green);
}
.model-btn.active {
  background: var(--sage-green);
  border-color: var(--sage-green);
  color: #fff;
  font-weight: 500;
}
.model-btn.disabled,
.model-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  border-color: #d1d5db;
  color: var(--sage-text-muted);
  background: transparent;
}
.model-btn.disabled:hover,
.model-btn:disabled:hover {
  border-color: #d1d5db;
  color: var(--sage-text-muted);
}

@media (prefers-reduced-motion: reduce) {
  .stop-btn { animation: none; }
  .send-btn, .stop-btn, .model-btn { transition: none; }
}
</style>
